import './assets/main.css'
import 'vue-cal/dist/vuecal.css'
import { captureBeforeInstallPrompt } from './utils/pwaTools'
import { trackServiceWorkerRegistrationAndUpdate } from './utils/registerSW'

captureBeforeInstallPrompt()
trackServiceWorkerRegistrationAndUpdate()

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueCal from 'vue-cal'
import { initializeSentry } from './utils/sentry'
import App from './App.vue'
import router from './router'
import './firebase' // Import Firebase setup (initializes app and connects to emulators)
import { createSentryPiniaPlugin } from '@sentry/vue'
import { scrubPiniaStateForSentryCustomerStores } from './utils/sentryPiniaStoreScrub'
import { isSentryOutboundPiiScrubEnabled } from './utils/sentryPiiScrubSwitch'
import { useFirebaseMessagingStore } from './stores/firebaseMessaging'
import { usePWAStore } from './stores/pwa'
import { useAnalyticsStore } from './stores/analytics'
import { useToastStore } from './stores/toast'
import {
  isIndexedDbConnectionLostError,
  isIndexedDbMissingObjectStoreError,
} from './utils/indexedDbTransientErrors'
import {
  logRecoveryOnce,
  notifyGlobalToastOnce,
  RECOVERY_SESSION_KEYS,
  tryOneTimeSessionReload,
} from './utils/recoveryUi'
import { handleChunkLoadError, isChunkLoadError } from './utils/versionCheck'
import { initClientMonitoring } from './utils/reloadLoopMonitor'
import { registerSwAuthTelemetryMainSink } from './utils/swAuthTelemetry'
import { reportSwAuthTelemetryToSentry } from './utils/sentrySwAuth'

const pinia = createPinia()
pinia.use(
  createSentryPiniaPlugin({
    stateTransformer: (state) =>
      isSentryOutboundPiiScrubEnabled() ? scrubPiniaStateForSentryCustomerStores(state) : state,
  }),
)

const app = createApp(App)

app.use(pinia)
app.use(router)

initializeSentry(app, router)
registerSwAuthTelemetryMainSink(reportSwAuthTelemetryToSentry)
initClientMonitoring()

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    event.preventDefault()
    void handleChunkLoadError(event.reason)
    return
  }

  // Transient IDB server loss (WebKit/Chromium, memory pressure). One reload per session, then warn.
  if (isIndexedDbConnectionLostError(event.reason)) {
    event.preventDefault()
    if (!tryOneTimeSessionReload(RECOVERY_SESSION_KEYS.idbConnectionLost)) {
      notifyGlobalToastOnce(
        RECOVERY_SESSION_KEYS.idbConnectionLost,
        'warning',
        "We couldn't load local data. Please refresh the page. If this keeps happening, try closing other tabs or restarting your browser.",
      )
    }
    return
  }

  // Missing IDB object stores on reload (Firebase / evidence cache schema race). App usually
  // recovers without user action — log once internally, no toast or extra reload.
  if (isIndexedDbMissingObjectStoreError(event.reason)) {
    event.preventDefault()
    logRecoveryOnce(
      RECOVERY_SESSION_KEYS.idbMissingObjectStore,
      'IndexedDB object store missing during startup (handled internally; no user action required).',
      event.reason,
    )
  }
})

app.component('vue-cal', VueCal)

app.mount('#app')

// Initialize Firebase Cloud Messaging

const firebaseMessagingStore = useFirebaseMessagingStore()
firebaseMessagingStore.scheduleInitializeFcm()

// Initialize PWA
const pwaStore = usePWAStore()
pwaStore.init()

// Initialize Analytics
const analyticsStore = useAnalyticsStore()
analyticsStore.initialize()
// Initialize Toast Store and make available globally without imports
const toastStore = useToastStore()

// Make info available globally without imports
;(globalThis as typeof globalThis & { toastInfo: typeof toastStore.info }).toastInfo =
  toastStore.info
;(globalThis as typeof globalThis & { toastSuccess: typeof toastStore.success }).toastSuccess =
  toastStore.success
;(globalThis as typeof globalThis & { toastError: typeof toastStore.error }).toastError =
  toastStore.error
;(globalThis as typeof globalThis & { toastWarning: typeof toastStore.warning }).toastWarning =
  toastStore.warning
;(globalThis as typeof globalThis & { toastDebug: typeof toastStore.debug }).toastDebug =
  toastStore.debug
